import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface State {
  hasError: boolean;
  message?: string;
}

/**
 * Top-level route error boundary. Prevents a deep component crash from blanking
 * the page (which kills SEO crawl status). Renders a helpful fallback with
 * navigation back to safe routes.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err?.message };
  }

  componentDidCatch(err: Error, info: ErrorInfo) {
    // Keep this lightweight; analytics/Sentry can hook in here later.
    console.error("[ErrorBoundary]", err, info);
  }

  reset = () => this.setState({ hasError: false, message: undefined });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <main className="min-h-dvh flex items-center justify-center px-4 bg-background">
        <div className="max-w-md text-center">
          <p className="text-sm uppercase tracking-wider text-primary font-bold mb-3">Something went wrong</p>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            We hit an unexpected error
          </h1>
          <p className="text-muted-foreground mb-8">
            Try reloading, or jump back to a safe page. Our team is notified automatically.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button onClick={() => { this.reset(); window.location.reload(); }}>Reload page</Button>
            <Button variant="outline" asChild>
              <Link to="/" onClick={this.reset}>Back to home</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/areas-of-expertise" onClick={this.reset}>Browse services</Link>
            </Button>
          </div>
        </div>
      </main>
    );
  }
}

export default ErrorBoundary;
